import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Flame, Newspaper, Mail } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "../components/PageHero";
import { AIContent } from "../components/AIContent";
import { T } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const weekLabel = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const week = Math.ceil(((now - start) / 86400000 + start.getDay() + 1) / 7);
  return `Week ${week}, ${now.getFullYear()}`;
};

export default function DigestPage() {
  const [trending, setTrending] = useState([]);
  const [news, setNews] = useState([]);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/digest/subscribe`, { email });
      setSubscribed(true);
      toast.success(res.data.already_subscribed ? "You're already on the list!" : "Subscribed! The digest lands every Monday.");
    } catch {
      toast.error("Please enter a valid email address");
    }
  };

  useEffect(() => {
    axios.get(`${API}/trending`).then((r) => setTrending(r.data)).catch(() => {});
    axios.get(`${API}/news`).then((r) => setNews(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const ready = trending.length > 0 && news.length > 0;
  const context = ready
    ? `Trending on SportsPulse this week: ${trending.map((t) => `${t.name} (${t.type})`).join(", ")}. Top headlines: ${news.map((n) => `"${n.title}" [${n.label}]`).join("; ")}. Weave these into one flowing weekly roundup with sections for what's buzzing, headline stories and what to watch next week. Only reference the provided items — do not invent scores or new stories.`
    : "";

  return (
    <div>
      <PageHero topic="sports week highlights collage" title="The SportsPulse Weekly Digest" subtitle={`Your AI-written roundup of what the world played, watched and talked about — ${weekLabel()}. Press Listen and enjoy it hands-free.`} listenTargetId="page-content" />
      <div id="page-content" className="mx-auto max-w-7xl space-y-10 px-4 py-12 sm:px-6">
        {ready && (
          <AIContent topic={`SportsPulse Weekly Digest — ${weekLabel()}`} context={context} />
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-orange-200 bg-orange-50 p-6" data-testid="digest-trending">
            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900"><Flame className="h-5 w-5 text-orange-600" /> <T>What's Buzzing</T></h2>
            <div className="mt-4 space-y-2">
              {trending.map((it, i) => (
                <Link key={it.key} to={it.link} data-testid={`digest-trending-${it.key}`} className="flex items-center justify-between rounded-xl bg-white px-4 py-3 transition-all hover:-translate-y-0.5 hover:shadow-sm">
                  <span className="flex items-center gap-3">
                    <span className="w-6 text-sm font-extrabold text-orange-500">#{i + 1}</span>
                    <span className="text-sm font-bold text-slate-800">{it.name}</span>
                    <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase text-orange-700">{it.type}</span>
                  </span>
                  <span className="text-xs font-semibold text-slate-400">{it.views >= 1000 ? `${(it.views / 1000).toFixed(1)}k` : it.views} <T>views</T></span>
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6" data-testid="digest-news">
            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900"><Newspaper className="h-5 w-5 text-[#0055FF]" /> <T>Headline Stories</T></h2>
            <div className="mt-4 space-y-2">
              {news.map((n) => (
                <div key={n.id} className="rounded-xl bg-white px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase text-blue-700"><T>{n.label}</T></span>
                    <span className="text-xs text-slate-400">{n.published}</span>
                  </div>
                  <p className="mt-1.5 text-sm font-bold text-slate-800"><T>{n.title}</T></p>
                </div>
              ))}
            </div>
            <Link to="/news" data-testid="digest-all-news" className="mt-4 inline-block text-sm font-bold text-[#0055FF] hover:underline"><T>All news</T> →</Link>
          </div>
        </div>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 sm:p-8" data-testid="digest-signup">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900"><Mail className="h-5 w-5 text-emerald-600" /> <T>Get the digest every Monday</T></h2>
              <p className="mt-1 text-sm text-slate-600"><T>One email a week — trending sport, headline stories and what to watch. No spam.</T></p>
            </div>
            {subscribed ? (
              <p className="rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white" data-testid="digest-subscribed"><T>You're on the list!</T> ✓</p>
            ) : (
              <form onSubmit={subscribe} className="flex w-full max-w-md gap-2">
                <input data-testid="digest-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="flex-1 rounded-full border border-emerald-300 bg-white px-5 py-2.5 text-sm outline-none focus:border-emerald-500" />
                <button data-testid="digest-subscribe-button" type="submit" className="rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-700"><T>Subscribe</T></button>
              </form>
            )}
          </div>
        </div>

        <p className="text-sm italic text-slate-400"><T>The digest is AI-written from curated demo data and refreshes weekly. Listen audio is generated from the current published version.</T></p>
      </div>
    </div>
  );
}
